import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * Pago de Celula via Zelle (semi-manual).
 *
 * Flujo:
 * 1. Usuario elige nivel -> backend crea orden con codigo unico WEN-XXXXXX
 * 2. UI muestra email/telefono receptor + monto + codigo (obligatorio en concepto)
 * 3. Usuario hace el Zelle desde su banco USA y pulsa "Ya envie"
 * 4. Estado pasa a awaiting_verification; admin confirma desde panel admin
 * 5. Polling cada 10s; al confirmarse, activa la Celula
 */
const ZellePaymentPanel = ({ levels, onSuccess }) => {
  const [config, setConfig] = useState(null);
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/zelle/config`).then(r => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);

  const createOrder = async (levelId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/zelle/order`, { level: levelId });
      setOrder(res.data);
      setStatus({ status: 'awaiting_send' });
      pollStatus(res.data.order_id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error creando orden Zelle');
    } finally {
      setLoading(false);
    }
  };

  const pollStatus = (orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/zelle/order/${orderId}/status`);
        setStatus(res.data);
        if (['confirmed', 'rejected', 'expired'].includes(res.data.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (res.data.status === 'confirmed' && onSuccess) onSuccess(res.data);
        }
      } catch (e) { /* ignore */ }
    }, 10000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const markSent = async () => {
    if (!order) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/zelle/order/${order.order_id}/i-sent-it`, {});
      setStatus(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error notificando envio');
    } finally {
      setSending(false);
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(String(text));

  if (!config) return null;
  if (!config.enabled) {
    return (
      <div data-testid="zelle-disabled" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Pago por Zelle no esta disponible aun. El operador esta configurando su cuenta bancaria estadounidense.
      </div>
    );
  }

  // Pantalla de orden activa
  if (order) {
    const st = status?.status || 'awaiting_send';
    const isConfirmed = st === 'confirmed';
    const isRejected = st === 'rejected';
    const isExpired = st === 'expired';
    const isPendingSend = st === 'awaiting_send';
    const isAwaitingVerif = st === 'awaiting_verification';

    return (
      <div className="rounded-2xl border-2 border-blue-200 bg-white p-5 space-y-4" data-testid="zelle-order-panel">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Pago Zelle</h3>
            <p className="text-xs text-gray-500">Servicio entre bancos de Estados Unidos</p>
          </div>
          {isConfirmed && <span className="px-3 py-1 rounded-full bg-green-600 text-white text-xs font-bold" data-testid="zelle-status-confirmed">CONFIRMADO</span>}
          {isAwaitingVerif && <span className="px-3 py-1 rounded-full bg-amber-600 text-white text-xs font-bold animate-pulse" data-testid="zelle-status-verifying">VERIFICANDO</span>}
          {isRejected && <span className="px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold" data-testid="zelle-status-rejected">RECHAZADO</span>}
          {isExpired && <span className="px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold">EXPIRADA</span>}
          {isPendingSend && <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold" data-testid="zelle-status-pending">PENDIENTE DE ENVIO</span>}
        </div>

        {(isPendingSend || isAwaitingVerif) && (
          <div className="space-y-3 text-sm">
            {config.email && (
              <div>
                <div className="text-[11px] text-gray-500 mb-1">EMAIL ZELLE</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-2 py-1.5 rounded break-all" data-testid="zelle-email">{config.email}</code>
                  <button onClick={() => copy(config.email)} className="text-xs px-2 py-1 bg-blue-600 text-white rounded">Copiar</button>
                </div>
              </div>
            )}
            {config.phone && (
              <div>
                <div className="text-[11px] text-gray-500 mb-1">TELEFONO ZELLE</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-2 py-1.5 rounded" data-testid="zelle-phone">{config.phone}</code>
                  <button onClick={() => copy(config.phone)} className="text-xs px-2 py-1 bg-blue-600 text-white rounded">Copiar</button>
                </div>
              </div>
            )}
            <div>
              <div className="text-[11px] text-gray-500 mb-1">NOMBRE DEL RECEPTOR</div>
              <code className="block bg-gray-100 px-2 py-1.5 rounded">{config.receiver_name}</code>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 mb-1">MONTO EXACTO</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-bold text-lg bg-gray-100 px-2 py-1.5 rounded" data-testid="zelle-amount">${order.amount_usd.toFixed(2)} USD</code>
                <button onClick={() => copy(order.amount_usd.toFixed(2))} className="text-xs px-2 py-1 bg-blue-600 text-white rounded">Copiar</button>
              </div>
            </div>
            <div className="border-2 border-red-300 bg-red-50 rounded p-3">
              <div className="text-[11px] text-red-700 font-bold mb-1">CODIGO DE REFERENCIA (OBLIGATORIO EN CONCEPTO/MEMO)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-bold text-lg bg-white px-2 py-1.5 rounded border border-red-300" data-testid="zelle-reference">{order.reference_code}</code>
                <button onClick={() => copy(order.reference_code)} className="text-xs px-2 py-1 bg-red-600 text-white rounded">Copiar</button>
              </div>
              <p className="text-[11px] text-red-700 mt-2 leading-tight">
                Sin este codigo en el concepto del Zelle no podremos identificar tu pago.
                Escribelo EXACTAMENTE como aparece arriba.
              </p>
            </div>
          </div>
        )}

        {isPendingSend && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800 space-y-1">
            <p className="font-bold">Instrucciones:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Abre tu app bancaria estadounidense con Zelle activo.</li>
              <li>Envia <strong>${order.amount_usd.toFixed(2)} USD</strong> a <strong>{config.email || config.phone}</strong>.</li>
              <li>En el campo concepto/memo escribe: <strong>{order.reference_code}</strong></li>
              <li>Cuando termines, pulsa el boton "Ya envie el Zelle".</li>
            </ol>
            <button
              onClick={markSent}
              disabled={sending}
              data-testid="zelle-i-sent-it"
              className="mt-3 w-full py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? 'Notificando...' : 'Ya envie el Zelle'}
            </button>
          </div>
        )}

        {isAwaitingVerif && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
            <p className="font-semibold">{'\u23F3'} Verificando tu pago...</p>
            <p className="mt-1">El operador revisa los Zelle entrantes y confirma manualmente. Esto puede tardar hasta 24 horas.
              Te llegara una notificacion en la app cuando tu Celula este activa.</p>
            <p className="mt-1 text-[10px]">Puedes cerrar esta pantalla y seguir explorando CUALAPP mientras tanto.</p>
          </div>
        )}

        {isConfirmed && (
          <div className="bg-green-50 border border-green-300 rounded-xl p-4 text-center" data-testid="zelle-success">
            <div className="text-3xl mb-2">{'\u2713'}</div>
            <p className="font-bold text-green-800">Celula activada</p>
            <p className="text-xs text-green-700 mt-1">{order.cualcoin_to_receive} Cc + {order.pv_breakdown?.net_usd} PV netos</p>
          </div>
        )}

        {isRejected && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-sm" data-testid="zelle-rejected">
            <p className="font-bold text-red-800">Pago rechazado</p>
            {status?.admin_note && <p className="text-xs text-red-700 mt-1">Motivo: {status.admin_note}</p>}
            <button onClick={() => { setOrder(null); setStatus(null); }} className="mt-2 px-4 py-1.5 rounded bg-red-600 text-white text-sm">
              Crear nueva orden
            </button>
          </div>
        )}

        {isExpired && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-center">
            <p className="text-red-800 font-semibold">Ventana de pago expirada</p>
            <button onClick={() => { setOrder(null); setStatus(null); }} className="mt-2 px-4 py-1.5 rounded bg-red-600 text-white text-sm">
              Crear nueva orden
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-blue-100 bg-blue-50/30 p-5" data-testid="zelle-selector">
      <h3 className="text-base font-bold text-blue-900">Pagar con Zelle</h3>
      <p className="text-xs text-blue-700 mb-3">
        Para usuarios con cuenta bancaria de Estados Unidos. Verificacion manual (hasta 24h).
      </p>
      {error && <div data-testid="zelle-error" className="text-sm text-red-700 bg-red-50 rounded p-2 mb-3">{error}</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {levels.map(l => (
          <button
            key={l.id}
            onClick={() => createOrder(l.id)}
            disabled={loading}
            data-testid={`zelle-buy-${l.id}`}
            className="p-3 rounded-lg border border-blue-200 bg-white hover:border-blue-400 text-left disabled:opacity-50"
          >
            <div className="text-xs font-semibold text-blue-800">{l.name}</div>
            <div className="text-lg font-bold text-blue-900">${l.price_usd}</div>
            <div className="text-[10px] text-blue-700">{l.cualcoin_included} Cc</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ZellePaymentPanel;
